import { FC, useEffect, useMemo, useState } from 'react';
import { CustomModal, InfiniteLoadingSpinner } from 'components';
import { CheckboxTickIcon, CircleCloseIcon } from 'assets/icons';
import { Tag } from 'containers/tags/tags.model';
import CustomCheckbox from 'components/custom-checkbox/CustomCheckbox';
import CustomButton, { ButtonType } from 'components/custom-button/CustomButton';
import SearchBar from 'components/search/SearchBar';
import TagPill from 'containers/tags/create-tag/Tag';

interface ModifyTagsModalProps {
  open: boolean;
  onClose: () => void;
  allTags: Tag[];
  isLoading: boolean;
  onUpdateTags: (selectedTags: Tag[], removeCurrentTags: boolean) => void;
  isSaving?: boolean;
}

const ModifyTagsModal: FC<ModifyTagsModalProps> = ({ open, onClose, allTags, isLoading, onUpdateTags, isSaving }) => {
  const [search, setSearch] = useState('');
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);
  const [removeCurrentTags, setRemoveCurrentTags] = useState(false);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setSelectedTags([]);
      setRemoveCurrentTags(false);
    }
  }, [open]);

  const filteredTags = useMemo(
    () => allTags.filter(tag => tag.name.toLowerCase().includes(search.toLowerCase())),
    [allTags, search]
  );

  const handleToggleTag = (tag: Tag) => {
    if (selectedTags.some(t => t.id === tag.id)) {
      setSelectedTags(selectedTags.filter(t => t.id !== tag.id));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const handleUpdate = () => {
    onUpdateTags(selectedTags, removeCurrentTags);
  };

  let content;
  if (isLoading) {
    content = <InfiniteLoadingSpinner />;
  } else if (filteredTags.length === 0) {
    content = <div className="p-4 text-center text-sbase text-accentText">No results found!</div>;
  } else {
    content = filteredTags.map(tag => (
      <div
        key={tag.id}
        className="flex cursor-pointer items-center"
        onClick={() => {
          handleToggleTag(tag);
        }}>
        <CustomCheckbox
          id={`tag-checkbox-${tag.id}`}
          width="min-w-[18px]"
          isChecked={selectedTags.some(t => t.id === tag.id)}
          onClick={() => handleToggleTag(tag)}
          className="mr-2">
          {selectedTags.some(t => t.id === tag.id) && <CheckboxTickIcon className="text-primaryBtn " />}
        </CustomCheckbox>
        <div className="flex flex-1 items-center gap-2">
          <TagPill tag={tag} className="px-2.5 py-1" textStyle="text-sbase font-semibold" />
        </div>
      </div>
    ));
  }

  return (
    <CustomModal open={open} closeModal={onClose} width="md:w-[492px]" height="h-[547px]">
      <div className="w-full rounded-md bg-white py-6">
        <div className="mb-6 flex items-center justify-between px-6">
          <div className="flex flex-col gap-1">
            <div className="text-xl font-bold text-headingGray">Modify tags</div>
          </div>
          <CircleCloseIcon className="h-6 w-6 cursor-pointer" onClick={onClose} />
        </div>

        <div className="mb-2 px-6">
          <div className="text-sbase font-semibold text-headingGray">Add tags</div>
        </div>

        <div className="mx-6 rounded border border-borderGray pt-4">
          <SearchBar
            id="tag-search"
            value={search}
            handleChange={setSearch}
            placeholder="Search tags"
            wrapperStyle="mb-3 border border-borderGray rounded-[4px] mx-3 h-[29px] w-[404px]"
            showSearchIcon={true}
            handleClear={() => setSearch('')}
            showCrossIcon={true}
            inputStyle="bg-transparent"
            autoFocus={true}
            magnifierIconStyle="w-5 h-5"
          />
          <div className="m-3 border-t border-borderGray" />
          <div
            className="customNormalScroll max-h-[204px] min-h-[204px] space-y-[10px] 
          overflow-y-auto bg-white pl-3">
            {content}
          </div>
        </div>
        <div className="mx-6 my-5 border-t border-borderGray" />
        <div className="mt-4 px-6">
          <div className="flex items-start gap-2">
            <CustomCheckbox
              id="remove-current-tags"
              isChecked={removeCurrentTags}
              onClick={() => setRemoveCurrentTags(!removeCurrentTags)}>
              {removeCurrentTags && <CheckboxTickIcon className="text-primaryBtn" />}
            </CustomCheckbox>
            <div className="flex flex-col">
              <span className="text-sbase font-semibold text-headingGray">Remove current tags</span>
              <span className="mt-1 text-[13px] text-accentText">
                Selecting this will remove all existing tags from the selected jobs
              </span>
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3 px-6">
          <CustomButton id="tag-cancel-btn" type={ButtonType.SECONDARY} onClick={onClose} className="h-10 w-[100px]">
            Cancel
          </CustomButton>
          <CustomButton
            id="tag-update-btn"
            isLoading={isSaving}
            type={ButtonType.PRIMARY}
            onClick={handleUpdate}
            className="h-10 w-[100px]">
            Update
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default ModifyTagsModal;
